import React, { useRef, useState } from "react";
import {
    Box,
    TableCell,
    TableRow,
    Chip,
    Typography,
    Select,
    MenuItem,
    FormControl,
    TextField,
    CircularProgress,
    IconButton,
} from "@mui/material";
import { Check, Cancel } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
// import { motion } from "framer-motion";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";

import { FlowTask, TaskPriority } from "../../interfaces/flowTask.interface";
import { STATUS_COLORS, PRIORITY_COLORS, TASK_STATUSES, TASK_PRIORITIES } from "../../constants";
import { useUpdateTodoTask } from "../../hooks/useUpdateTodoTask";
import { colors } from "../../../ui/colors";

interface ListViewRowProps {
    task: FlowTask;
}

const getSelectMenuProps = () => ({
    disablePortal: false,
    container: () => {
        let container = document.getElementById("flow-do-select-container");
        if (!container) {
            container = document.createElement("div");
            container.id = "flow-do-select-container";
            container.style.zIndex = "99999999";
            container.style.position = "relative";
            document.body.appendChild(container);
        }
        return container;
    },
    PaperProps: {
        sx: {
            zIndex: "99999999 !important" as any,
            position: "fixed",
        },
        style: {
            zIndex: 99999999,
            position: "fixed" as const,
        },
    },
});

export const ListViewRow = ({ task }: ListViewRowProps) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isEditingDeadline, setIsEditingDeadline] = useState(false);

    // Local state for optimistic updates / editing
    const [titleValue, setTitleValue] = useState(task.name);
    const [notesValue, setNotesValue] = useState(task.description || "");
    const [deadlineValue, setDeadlineValue] = useState<Dayjs | null>(
        task.deadline ? dayjs(task.deadline) : null
    );

    const updateTaskMutation = useUpdateTodoTask();
    const notesSavingRef = useRef(false);

    // Sync state with props when not editing
    React.useEffect(() => {
        if (!isEditingTitle) setTitleValue(task.name);
        if (!isEditingNotes && !notesSavingRef.current) {
            setNotesValue(task.description || "");
        }
        if (!isEditingDeadline) {
            setDeadlineValue(task.deadline ? dayjs(task.deadline) : null);
        }
    }, [task, isEditingTitle, isEditingNotes, isEditingDeadline]);


    const isUpdating = updateTaskMutation.isPending;

    const handleStatusChange = (newStatus: FlowTask["status"]) => {
        updateTaskMutation.mutate({
            taskId: task.uid,
            updates: { status: newStatus },
        });
    };

    const handlePriorityChange = (newPriority: TaskPriority) => {
        updateTaskMutation.mutate({
            taskId: task.uid,
            updates: { priority: newPriority },
        });
    };

    const handleTitleBlur = () => {
        const newTitle = titleValue.trim();
        if (newTitle && newTitle !== task.name) {
            updateTaskMutation.mutate({
                taskId: task.uid,
                updates: { name: newTitle },
            });
        } else {
            setTitleValue(task.name);
        }
        setIsEditingTitle(false);
    };

    const handleNotesConfirm = () => {
        const newNotes = notesValue.trim();
        const currentNotes = task.description || "";

        if (newNotes !== currentNotes) {
            notesSavingRef.current = true;
            updateTaskMutation.mutate(
                {
                    taskId: task.uid,
                    updates: { description: newNotes || undefined },
                },
                {
                    onSettled: () => {
                        notesSavingRef.current = false;
                        setIsEditingNotes(false);
                    },
                    onSuccess: (updatedTask) => {
                        setNotesValue(updatedTask.description || "");
                    }
                }
            );
        } else {
            setIsEditingNotes(false);
        }
    };

    const handleNotesCancel = () => {
        setNotesValue(task.description || "");
        setIsEditingNotes(false);
    };

    const handleDeadlineChange = (newValue: Dayjs | null) => {
        setDeadlineValue(newValue);
        const deadlineString = newValue ? newValue.toISOString() : undefined;
        updateTaskMutation.mutate({
            taskId: task.uid,
            updates: { deadline: deadlineString },
        });
        setIsEditingDeadline(false);
    };

    const formatDeadline = (deadline?: string) => {
        if (!deadline) return "-";
        try {
            return format(new Date(deadline), "MMM dd, yyyy", { locale: enUS });
        } catch {
            return "-";
        }
    };

    const statusColor = STATUS_COLORS[task.status];
    const priorityColor = PRIORITY_COLORS[task.priority];

    return (
        <TableRow
            sx={{
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                "&:hover": {
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                },
                position: "relative",
            }}
        >
            {/* Loading Overlay */}
            {isUpdating && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.6)",
                        zIndex: 1,
                        backdropFilter: "blur(1px)",
                    }}
                >
                    <CircularProgress size={20} thickness={4} sx={{ color: "var(--fx-surface-text, #0F172A)" }} />
                </Box>
            )}

            {/* Status */}
            <TableCell sx={{ py: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={task.status}
                        onChange={(e) => handleStatusChange(e.target.value as FlowTask["status"])}
                        disabled={isUpdating}
                        MenuProps={getSelectMenuProps()}
                        sx={{
                            height: 28,
                            fontSize: "0.75rem",
                            bgcolor: "transparent",
                            color: statusColor.color,
                            fontWeight: 600,
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: "none" },
                            "&:hover": { bgcolor: "transparent" },
                            "& .MuiSelect-select": {
                                paddingLeft: 0,
                                display: 'flex',
                                alignItems: 'center'
                            }
                        }}
                        inputProps={{ IconComponent: () => null }} // Hide arrow for cleaner look or keep it? Keeping logic similar to card for now
                        displayEmpty
                        renderValue={(selected) => (
                            <Chip
                                label={selected.replace("_", " ")}
                                size="small"
                                sx={{
                                    bgcolor: STATUS_COLORS[selected as any].bg,
                                    color: STATUS_COLORS[selected as any].color,
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    height: 22,
                                    cursor: "pointer",
                                    "&:hover": { opacity: 0.9 }
                                }}
                            />
                        )}
                    >
                        {TASK_STATUSES.map((status) => {
                            const sColor = STATUS_COLORS[status];
                            return (
                                <MenuItem key={status} value={status}>
                                    <Chip
                                        label={status.replace("_", " ")}
                                        size="small"
                                        sx={{
                                            bgcolor: sColor.bg,
                                            color: sColor.color,
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                            height: 22,
                                        }}
                                    />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </TableCell>

            {/* Title & Notes */}
            <TableCell sx={{ width: "40%", py: 1.5 }}>
                <Box>
                    {isEditingTitle ? (
                        <TextField
                            value={titleValue}
                            onChange={(e) => setTitleValue(e.target.value)}
                            onBlur={handleTitleBlur}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Enter") handleTitleBlur();
                            }}
                            autoFocus
                            disabled={isUpdating}
                            size="small"
                            fullWidth
                            sx={{
                                mb: 0.5,
                                "& .MuiOutlinedInput-root": {
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    p: 0,
                                    "& fieldset": { border: "none" },
                                    "& input": { p: 0.5 }
                                },
                            }}
                        />
                    ) : (
                        <Typography
                            variant="body2"
                            onClick={() => setIsEditingTitle(true)}
                            sx={{
                                fontWeight: 600,
                                color: "var(--fx-surface-text, #0F172A)",
                                mb: 0.5,
                                cursor: "text",
                                "&:hover": {
                                    color: "var(--fx-surface-text, #0F172A)",
                                },
                                width: "fit-content"
                            }}
                        >
                            {task.name}
                        </Typography>
                    )}

                    {isEditingNotes ? (
                        <Box>
                            <TextField
                                value={notesValue}
                                onChange={(e) => setNotesValue(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === "Escape") handleNotesCancel();
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        handleNotesConfirm();
                                    }
                                }}
                                disabled={isUpdating}
                                multiline
                                rows={2}
                                size="small"
                                fullWidth
                                placeholder="Add notes..."
                                sx={{
                                    mt: 0.5,
                                    "& .MuiOutlinedInput-root": {
                                        fontSize: "0.75rem",
                                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                                    },
                                }}
                            />
                            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, justifyContent: "flex-end" }}>
                                <IconButton
                                    size="small"
                                    onClick={handleNotesConfirm}
                                    disabled={isUpdating}
                                    sx={{
                                        bgcolor: colors.success[50],
                                        color: colors.success[700],
                                        "&:hover": { bgcolor: colors.success[100] },
                                        width: 24, height: 24,
                                    }}
                                >
                                    <Check sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={handleNotesCancel}
                                    disabled={isUpdating}
                                    sx={{
                                        bgcolor: colors.gray[100],
                                        color: colors.gray[700],
                                        "&:hover": { bgcolor: colors.gray[200] },
                                        width: 24, height: 24,
                                    }}
                                >
                                    <Cancel sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Typography
                            variant="caption"
                            onClick={() => setIsEditingNotes(true)}
                            sx={{
                                color: notesValue ? "var(--fx-surface-textsecondary, #64748B)" : "var(--fx-surface-texttertiary, #94A3B8)",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "400px",
                                cursor: "text",
                                "&:hover": {
                                    color: "var(--fx-surface-textsecondary, #64748B)",
                                },
                                fontStyle: notesValue ? "normal" : "italic"
                            }}
                        >
                            {notesValue || "Click to add notes..."}
                        </Typography>
                    )}
                </Box>
            </TableCell>

            {/* Priority */}
            <TableCell sx={{ py: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                    <Select
                        value={task.priority}
                        onChange={(e) => handlePriorityChange(e.target.value as TaskPriority)}
                        disabled={isUpdating}
                        MenuProps={getSelectMenuProps()}
                        sx={{
                            height: 22,
                            fontSize: "0.65rem",
                            bgcolor: "transparent",
                            color: priorityColor.color,
                            fontWeight: 600,
                            ".MuiOutlinedInput-notchedOutline": { border: "none" },
                            "& .MuiSelect-select": { paddingLeft: 0, display: 'flex' }
                        }}
                        inputProps={{ IconComponent: () => null }}
                        renderValue={(selected) => (
                            <Chip
                                label={selected}
                                size="small"
                                sx={{
                                    bgcolor: PRIORITY_COLORS[selected as any].bg,
                                    color: PRIORITY_COLORS[selected as any].color,
                                    fontWeight: 600,
                                    fontSize: "0.65rem",
                                    height: 20,
                                    cursor: "pointer",
                                    "&:hover": { opacity: 0.9 }
                                }}
                            />
                        )}
                    >
                        {TASK_PRIORITIES.map((priority) => {
                            const pColor = PRIORITY_COLORS[priority];
                            return (
                                <MenuItem key={priority} value={priority}>
                                    <Chip
                                        label={priority}
                                        size="small"
                                        sx={{
                                            bgcolor: pColor.bg,
                                            color: pColor.color,
                                            fontWeight: 600,
                                            fontSize: "0.65rem",
                                            height: 20,
                                        }}
                                    />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </TableCell>

            {/* Deadline */}
            <TableCell sx={{ py: 1.5 }}>
                {isEditingDeadline ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={deadlineValue}
                            onChange={handleDeadlineChange}
                            onClose={() => setIsEditingDeadline(false)}
                            disabled={isUpdating}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: {
                                        width: 140,
                                        "& .MuiInputBase-root": { fontSize: "0.875rem" },
                                    },
                                },
                                popper: { sx: { zIndex: "99999999 !important" as any } },
                            }}
                            format="MMM D, YYYY"
                        />
                    </LocalizationProvider>
                ) : (
                    <Typography
                        variant="body2"
                        onClick={() => setIsEditingDeadline(true)}
                        sx={{
                            color: "var(--fx-surface-textsecondary, #64748B)",
                            fontSize: "0.875rem",
                            cursor: "pointer",
                            "&:hover": {
                                color: "var(--fx-surface-text, #0F172A)",
                            },
                            width: "fit-content"
                        }}
                    >
                        {formatDeadline(task.deadline)}
                    </Typography>
                )}
            </TableCell>
        </TableRow>
    );
};
